// Custom
import "./newStudentForm.css";
import "../general.css";

// React Router
import { useParams } from "react-router-dom";

// Bootstrap Components
import Button from "react-bootstrap/Button";
import { Form } from "react-bootstrap";

// Firebase
import { getGroupData, signInWithCode } from "../../backend/firebaseFunctions";
import { useEffect, useState } from "react";

// Components
import { WelcomePhase } from "./Phases/WelcomePhase";
import StudentDetailsPhase from "./Phases/StudentDetailsPhase";
import ParentsDetailsPhase from "./Phases/ParentsDetailsPhase";
import DetailsApprovePhase from "./Phases/DetailsApprovePhase";
import PaymentCheckPhase from "./Phases/PaymentCheckPhase";

// Icons
import {
  FiCheck,
  FiUser,
  FiUsers,
  FiClipboard,
  FiCheckCircle,
  FiCreditCard,
  FiHome,
  FiLock,
  FiGlobe,
} from "react-icons/fi";

// Checks email validity
export function isValidEmail(email) {
  // Use a regular expression to check if the email address follows the proper format
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
  return emailRegex.test(email);
}

export function getAge(birthday) {
  // Get the current date
  const currentDate = new Date();

  // Calculate the person's age
  let age = currentDate.getFullYear() - birthday.getFullYear();
  if (
    currentDate.getMonth() < birthday.getMonth() ||
    (currentDate.getMonth() === birthday.getMonth() &&
      currentDate.getDate() < birthday.getDate())
  ) {
    age--;
  }

  // Return the age
  return Math.ceil(age);
}

export const getDaysString = (groupData) => {
  let str = "";
  if (groupData?.trainingDays) {
    str += "ימי ";
    groupData.trainingDays.map((day, index) => {
      str += day;
      if (index !== groupData.trainingDays.length - 1) {
        str += ", ";
      }
      return day;
    });
  } else if (groupData?.trainingSlots) {
    str = "";
    groupData.trainingSlots.map((slotObject, index) => {
      str += `יום `;
      str += slotObject.day;
      str += `'`;
      str += ` - `;
      str += slotObject.range;
      if (index !== groupData.trainingSlots.length - 1) {
        str += `&`;
      }
    });
  }
  return str;
};

const NewStudentForm = () => {
  const [verificationStatus, setVerificationStatus] = useState(
    "awaiting auth code submit"
  );
  const [error, setError] = useState(null);
  const [showSpinner, setShowSpinner] = useState(false);

  const [authCode, setAuthCode] = useState("");

  const initCodeAuth = async () => {
    setShowSpinner(true);

    try {
      let isCodeValid = await signInWithCode(authCode);
      if (isCodeValid) {
        setVerificationStatus("success");
      } else {
        setError("קוד מאמן שגוי, אנא נסה שנית");
        setVerificationStatus("awaiting auth code submit");
      }
    } catch (err) {
      setVerificationStatus("awaiting auth code submit");
    } finally {
      setShowSpinner(false);
    }
  };

  // getting the user or organization ID and the GroupID from the URL
  const { ID, groupID, userField } = useParams();

  // Setting states for the data fetching and page loading
  const [groupData, setGroupData] = useState();
  const [loaded, setLoaded] = useState(false);

  // Progress Bar state
  const [currentStep, setCurrentStep] = useState(1);

  const [newStudentData, setNewStudentData] = useState({});
  const [newParentsData, setNewParentsData] = useState({});

  const [studentPhoto, setStudentPhoto] = useState("");

  const [formState, setFormState] = useState("normal");

  // Centralized configuration for form flow
  const [formConfig, setFormConfig] = useState({
    hasPaymentUrl: false,
    hasPaymentCheck: false
  });

  // Update form config when group data changes
  useEffect(() => {
    if (groupData) {
      setFormConfig({
        hasPaymentUrl: !!(groupData.paymentUrl && groupData.paymentUrl.trim() !== ""),
        hasPaymentCheck: !!groupData.paymentCheck
      });
    }
  }, [groupData]);

  // Create dynamic steps based on group data
  const getSteps = () => {
    const baseSteps = [
      { icon: <FiHome />, label: "ברוכים הבאים" },
      { icon: <FiLock />, label: "אימות קוד" },
      { icon: <FiUser />, label: "פרטי תלמיד" },
      { icon: <FiUsers />, label: "פרטי הורים" },
    ];

    // Add payment link step only if paymentUrl exists and is not empty
    if (formConfig.hasPaymentUrl) {
      baseSteps.push({ icon: <FiGlobe />, label: "קישור לתשלום" });
    }

    // Add payment check step only if paymentCheck exists
    if (formConfig.hasPaymentCheck) {
      baseSteps.push({ icon: <FiCreditCard />, label: "אימות תשלום" });
    }

    // Add final steps
    baseSteps.push(
      { icon: <FiClipboard />, label: "אימות נתונים" },
      { icon: <FiCheckCircle />, label: "סיום" }
    );

    return baseSteps;
  };

  const steps = getSteps();

  // Helper function to determine which step should be shown
  const getCurrentPhase = () => {
    const hasPaymentUrl = formConfig.hasPaymentUrl;
    const hasPaymentCheck = formConfig.hasPaymentCheck;

    // Step 1: Welcome
    if (currentStep === 1) return 'welcome';

    // Step 2: Auth code
    if (currentStep === 2) return 'auth';

    // Step 3: Student details
    if (currentStep === 3) return 'student';

    // Step 4: Parents details
    if (currentStep === 4) return 'parents';

    // Step 5: Payment link (if exists) or Payment check (if no payment URL but has payment check) or Details approve
    if (currentStep === 5) {
      if (hasPaymentUrl) return 'paymentLink';
      if (hasPaymentCheck) return 'paymentCheck';
      return 'detailsApprove';
    }

    // Step 6: Payment check (if payment URL exists and has payment check) or Details approve (if payment URL exists but no payment check) or Details approve (if no payment URL but has payment check) or Success
    if (currentStep === 6) {
      if (hasPaymentUrl && hasPaymentCheck) return 'paymentCheck';
      if (hasPaymentUrl && !hasPaymentCheck) return 'detailsApprove';
      if (!hasPaymentUrl && hasPaymentCheck) return 'detailsApprove';
      return 'success';
    }

    // Step 7: Details approve (if both payment URL and payment check exist) or Success
    if (currentStep === 7) {
      if (hasPaymentUrl && hasPaymentCheck) return 'detailsApprove';
      return 'success';
    }

    // Step 8: Success (if both payment URL and payment check exist)
    if (currentStep === 8) return 'success';

    return 'welcome';
  };

  const getStepStatus = (stepIndex) => {
    if (currentStep > stepIndex + 1) return "completed";
    if (currentStep === stepIndex + 1) return "active";
    return "pending";
  };

  // Getting the user's groups data before rendering the page
  useEffect(() => {
    (async function () {
      let groupData = await getGroupData(ID, groupID);
      setGroupData(groupData);
      setLoaded(true);
    })();
  }, [ID, groupID, userField]);

  useEffect(() => {
    if (currentStep === 2 && verificationStatus !== "success") {
      setVerificationStatus("awaiting auth code submit");
      setShowSpinner(false);
    }
    setError(null);
  }, [currentStep]);

  useEffect(() => {
    if (error) setShowSpinner(false);
  }, [error]);

  // Add scroll to top effect when phase changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  return (
    <div
      className="form-container"
      dir="rtl"
      role="main"
      aria-label="טופס הרשמת תלמיד חדש"
    >
      <div className="form-card">
        {loaded ? (
          <>
            {groupData ? (
              <div
                className="phase-transition"
                role="region"
                aria-label="תהליך הרשמה"
              >
                <div
                  className="stepper"
                  role="navigation"
                  aria-label="שלבי ההרשמה"
                >
                  {steps.map((step, index) => (
                    <div
                      key={index}
                      className={`step ${getStepStatus(index)}`}
                      title={step.label}
                      role="tab"
                      aria-selected={currentStep === index + 1}
                      aria-label={`שלב ${index + 1}: ${step.label}`}
                      tabIndex={0}
                    >
                      {getStepStatus(index) === "completed" ? (
                        <FiCheck />
                      ) : (
                        step.icon
                      )}
                    </div>
                  ))}
                </div>

                {getCurrentPhase() === 'welcome' && (
                  <WelcomePhase
                    setProgress={setCurrentStep}
                    groupData={groupData}
                  />
                )}

                {getCurrentPhase() === 'auth' && (
                  <div
                    className="phase-transition"
                    role="region"
                    aria-label="אימות קוד מאמן"
                  >
                    {verificationStatus !== "success" && (
                      <h2 className="text-center mb-4" id="auth-code-heading">
                        הזן קוד מאמן
                      </h2>
                    )}
                    <Form
                      className="auth-form"
                      role="form"
                      aria-labelledby="auth-code-heading"
                    >
                      {verificationStatus === "awaiting auth code submit" && (
                        <Form.Group className="form-group">
                          <Form.Label
                            htmlFor="auth-code-input"
                            className="visually-hidden"
                          >
                            קוד מאמן
                          </Form.Label>
                          <Form.Control
                            id="auth-code-input"
                            className="form-control"
                            type="text"
                            value={authCode}
                            placeholder="קוד מאמן"
                            onChange={(e) => {
                              setError(null);
                              setAuthCode(e.target.value);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !showSpinner) {
                                e.preventDefault();
                                initCodeAuth();
                              }
                            }}
                            isInvalid={!!error}
                            aria-invalid={!!error}
                            aria-describedby={
                              error ? "auth-code-error" : undefined
                            }
                          />
                          {error && (
                            <div
                              id="auth-code-error"
                              className="error-message"
                              role="alert"
                            >
                              {error}
                            </div>
                          )}
                          <div className="d-flex justify-content-center mt-4">
                            {showSpinner ? (
                              <div
                                className="spinner"
                                role="status"
                                aria-label="טוען..."
                              >
                                <span className="visually-hidden">טוען...</span>
                              </div>
                            ) : (
                              <Button
                                variant="primary"
                                className="btn btn-primary"
                                onClick={initCodeAuth}
                                aria-label="המשך לאימות קוד"
                              >
                                המשך
                              </Button>
                            )}
                          </div>
                        </Form.Group>
                      )}

                      {verificationStatus === "success" && (
                        <div
                          className="text-center"
                          role="status"
                          aria-live="polite"
                        >
                          <div className="success-checkmark" aria-hidden="true">
                            <FiCheckCircle size={80} color="#39b54a" />
                          </div>
                          <h2 className="mt-4">אימתנו אותך!</h2>
                          <Button
                            variant="primary"
                            className="btn btn-primary mt-4"
                            onClick={() => setCurrentStep(3)}
                            aria-label="המשך לשלב הבא"
                          >
                            המשך
                          </Button>
                        </div>
                      )}
                    </Form>
                  </div>
                )}

                {getCurrentPhase() === 'student' && (
                  <div className="phase-transition">
                    <StudentDetailsPhase
                      setProgress={setCurrentStep}
                      newStudentData={newStudentData}
                      setNewStudentData={setNewStudentData}
                      studentPhoto={studentPhoto}
                      setStudentPhoto={setStudentPhoto}
                      userField={userField}
                      groupData={groupData}
                      formConfig={formConfig}
                    />
                  </div>
                )}

                {getCurrentPhase() === 'parents' && (
                  <div className="phase-transition">
                    <ParentsDetailsPhase
                      paymentCheck={groupData?.paymentCheck}
                      newStudentData={newStudentData}
                      setCurrentStep={setCurrentStep}
                      newParentsData={newParentsData}
                      setNewParentsData={setNewParentsData}
                      formConfig={formConfig}
                    />
                  </div>
                )}

                {getCurrentPhase() === 'paymentLink' && (
                  <div className="phase-transition">
                    <div className="text-center">
                      <h2 className="mb-4">קישור לתשלום</h2>
                      <p className="mb-4">כעת תועברו למסך התשלום בעבור הפעילות בקבוצה</p>
                      <div className="d-flex flex-column align-items-center gap-3">
                        <Button
                          variant="primary"
                          className="btn btn-primary"
                          onClick={() => {
                            // Add protocol if missing
                            let url = groupData.paymentUrl;
                            if (!url.startsWith('http://') && !url.startsWith('https://')) {
                              url = 'https://' + url;
                            }
                            window.open(url, '_blank');
                            // If there's a payment check, go to step 6, otherwise go to step 6 (details approve)
                            setCurrentStep(6);
                          }}
                          aria-label="פתח קישור לתשלום בחלון חדש"
                        >
                          קישור לתשלום
                        </Button>
                        <Button
                          variant="secondary"
                          className="newtext newsmallerText"
                          onClick={() => setCurrentStep(4)}
                        >
                          חזור
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {getCurrentPhase() === 'paymentCheck' && (
                  <div className="phase-transition">
                    <PaymentCheckPhase
                      setProgress={setCurrentStep}
                      newStudentData={newStudentData}
                      setNewStudentData={setNewStudentData}
                      groupData={groupData}
                      formConfig={formConfig}
                    />
                  </div>
                )}

                {getCurrentPhase() === 'detailsApprove' && (
                  <div className="phase-transition">
                    <DetailsApprovePhase
                      formState={formState}
                      setFormState={setFormState}
                      newStudentData={newStudentData}
                      newParentsData={newParentsData}
                      setCurrentStep={setCurrentStep}
                      paymentCheck={groupData?.paymentCheck}
                      ID={ID}
                      userField={userField}
                      groupID={groupID}
                      studentPhoto={studentPhoto}
                      groupData={groupData}
                      formConfig={formConfig}
                    />
                  </div>
                )}

                {getCurrentPhase() === 'success' && (
                  <div
                    className="phase-transition text-center"
                    role="status"
                    aria-live="polite"
                  >
                    <div className="success-checkmark" aria-hidden="true">
                      <FiCheckCircle size={80} color="#39b54a" />
                    </div>
                    <h2 className="mt-4" style={{ direction: "rtl" }}>
                      החניך נרשם בהצלחה!
                    </h2>
                    <h4 className="mt-4" style={{ direction: "rtl" }}>
                      ניתן לסגור את החלון
                    </h4>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center">
                <h2>קבוצה לא נמצאה</h2>
              </div>
            )}
          </>
        ) : (
          <div className="d-flex justify-content-center">
            <div className="spinner" />
          </div>
        )}
      </div>
    </div>
  );
};

export default NewStudentForm;
